import { Alert, Button, Center, Loader } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { errorMessage, isApiError } from "@/api";
import { NotFound } from "@/shell/NotFound";
import { NotPermitted } from "@/shell/NotPermitted";

import { EmptyState } from "./EmptyState";

type Props<T> = {
  query: Pick<UseQueryResult<T>, "data" | "error" | "isPending" | "refetch" | "isRefetching">;
  /** Render the empty state instead of children when this returns true. */
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  children: (data: T) => ReactNode;
};

/** Loading / error-with-retry / empty / data, for any react-query result (spec §7). */
export function QueryState<T>({ query, isEmpty, empty, children }: Props<T>) {
  if (query.isPending) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }
  if (query.error) {
    // Any 404 NOT_FOUND (own routes and upstream ones) is a plain not-found state, with the API's message.
    if (isApiError(query.error, "NOT_FOUND")) return <NotFound inline detail={query.error.message} />;
    if (isApiError(query.error, "FORBIDDEN")) return <NotPermitted inline />;
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Could not load" data-testid="query-error">
        {errorMessage(query.error)}
        <div>
          <Button
            mt="sm"
            size="xs"
            variant="light"
            color="red"
            loading={query.isRefetching}
            onClick={() => query.refetch()}
          >
            Retry
          </Button>
        </div>
      </Alert>
    );
  }
  const data = query.data as T;
  if (isEmpty?.(data)) return <>{empty ?? <EmptyState />}</>;
  return <>{children(data)}</>;
}
